import { Internationalization } from '@syncfusion/ej2-base';

import { updateBomDataRequest } from '../../../queries/apiTypes';
import { getAmountForUi, applyRounding } from '../../../shared/utils';
import { CALC_METHODS } from '../constants';
import { convertToCents, convertToDollar } from '../../../../shared/utils/utils';
import { MainProductDataType } from '../../mainProductTable';
import { ServicesResponseType } from '../../servicesModal/queries/apiTypes';
import { OptionDataType } from '../../optionsTable';
import { NO_ROUNDING, BOM_TABLES_ENUM, ROUNDING_SELL_PRICE_KEY } from '../../../shared/constants';
import { PricingBodInfo, RoundingBehaviour } from 'src/features/BOM/shared/types';

export interface PricingInfo {
    qty: number | null;
    cost: number | null;
    discount: number | null;
    discountedCost: number | null;
    margin: number | null;
    sellPrice: number | null;
    extendedPrice: number | null;
    extendedDiscountCost: number | null;
    per?: number | null;
    roundingBehaviourOnCell?: string;
    roundingPrecision?: RoundingBehaviour;
}

function handleCostChange({ cost, discount, margin, qty, per }: Pick<Partial<PricingInfo>, 'discount' | 'cost' | 'margin' | 'qty' | 'per'>) {
    const updatedRowInfo: Pick<Partial<PricingInfo>, 'discountedCost' | 'sellPrice' | 'extendedPrice' | 'discount' | 'extendedDiscountCost' | 'margin'> = {};
    if (!cost) {
        updatedRowInfo.discount = null;
        updatedRowInfo.discountedCost = null;
        updatedRowInfo.extendedDiscountCost = null;
        updatedRowInfo.margin = null;
    } else {
        updatedRowInfo.discount = discount ?? 0;
        updatedRowInfo.margin = margin ?? 0;

        const updatedInfoBasedOnCost = handleDiscountChange({ cost, discount: updatedRowInfo.discount, margin: updatedRowInfo.margin, qty, per });
        updatedRowInfo.discountedCost = updatedInfoBasedOnCost.discountedCost;
        updatedRowInfo.sellPrice = updatedInfoBasedOnCost.sellPrice;
        updatedRowInfo.extendedPrice = updatedInfoBasedOnCost.extendedPrice;
        updatedRowInfo.extendedDiscountCost = updatedInfoBasedOnCost.extendedDiscountCost;
    }
    return updatedRowInfo;
}

function handleDiscountChange({ cost, discount, margin, qty, per }: Pick<Partial<PricingInfo>, 'discount' | 'cost' | 'margin' | 'qty' | 'extendedDiscountCost' | 'per'>) {
    const updatedRowInfo: Pick<Partial<PricingInfo>, 'discountedCost' | 'extendedDiscountCost' | 'sellPrice' | 'extendedPrice'> = {};
    if (discount === null) {
        updatedRowInfo.discountedCost = null;
        updatedRowInfo.sellPrice = null;
        updatedRowInfo.extendedDiscountCost = null;
        updatedRowInfo.extendedPrice = null;
    }
    if (cost && (discount || discount === 0)) {
        updatedRowInfo.discountedCost = Number((cost * (1 - discount / 100)).toFixed(2));
        const updatedInfoBasedOnDiscountedCost = handleDiscountedCostChange({
            discountedCost: updatedRowInfo.discountedCost,
            extendedDiscountCost: updatedRowInfo.extendedDiscountCost,
            margin,
            qty,
            per
        });
        updatedRowInfo.sellPrice = updatedInfoBasedOnDiscountedCost.sellPrice;
        updatedRowInfo.extendedPrice = updatedInfoBasedOnDiscountedCost.extendedPrice;
        updatedRowInfo.extendedDiscountCost = updatedInfoBasedOnDiscountedCost.extendedDiscountCost;
    }

    return updatedRowInfo;
}

function handleDiscountedCostChange({
    discountedCost,
    cost,
    margin,
    qty,
    extendedDiscountCost,
    per
}: Pick<Partial<PricingInfo>, 'discountedCost' | 'extendedDiscountCost' | 'cost' | 'margin' | 'qty' | 'per'>) {
    const updatedRowInfo: Pick<Partial<PricingInfo>, 'discount' | 'sellPrice' | 'extendedPrice' | 'extendedDiscountCost'> = {};

    if (discountedCost === null) {
        updatedRowInfo.discount = null;
        updatedRowInfo.sellPrice = null;
        updatedRowInfo.extendedPrice = null;
        updatedRowInfo.extendedDiscountCost = null;
    }

    if ((discountedCost || discountedCost === 0) && (cost || cost === 0)) {
        const discount = Number(((1 - discountedCost / cost) * 100).toFixed(2));
        updatedRowInfo.discount = Number.isFinite(discount) ? discount : null;
    }

    if ((discountedCost || discountedCost === 0) && (margin || margin === 0)) {
        const updatedInfoBasedOnDiscountedCost = handleMarginChange({ discountedCost, margin, qty });
        updatedRowInfo.sellPrice = updatedInfoBasedOnDiscountedCost.sellPrice;
        updatedRowInfo.extendedPrice = updatedInfoBasedOnDiscountedCost.extendedPrice;
        updatedRowInfo.extendedDiscountCost = updatedInfoBasedOnDiscountedCost.extendedDiscountCost;
    }

    return updatedRowInfo;
}

function handleMarginChange({ discountedCost, margin, qty, per }: Pick<Partial<PricingInfo>, 'discountedCost' | 'margin' | 'qty' | 'per'>) {
    const updatedRowInfo: { sellPrice?: number | null; extendedPrice?: number | null; extendedDiscountCost?: number | null } = {};
    if ((discountedCost || discountedCost === 0) && (margin || margin === 0)) {
        const sellPrice = Number((discountedCost / (1 - margin / 100)).toFixed(2));
        updatedRowInfo.sellPrice = Number.isFinite(sellPrice) ? sellPrice : null;
        const updatedInfoBasedOnSellPrice = handleSellPriceChange({
            discountedCost,
            qty,
            sellPrice: updatedRowInfo.sellPrice
        });
        updatedRowInfo.extendedPrice = updatedInfoBasedOnSellPrice.extendedPrice;
        updatedRowInfo.extendedDiscountCost = updatedInfoBasedOnSellPrice.extendedDiscountCost;
    } else if (margin === null) {
        updatedRowInfo.sellPrice = null;
        updatedRowInfo.extendedPrice = null;
    }
    return updatedRowInfo;
}

function handleSellPriceChange({ discountedCost, sellPrice, qty, per }: Pick<Partial<PricingInfo>, 'discountedCost' | 'sellPrice' | 'qty' | 'per'>) {
    const updatedRowInfo: { margin?: number | null; extendedPrice?: number | null; extendedDiscountCost?: number | null } = {};
    if ((discountedCost || discountedCost === 0) && (sellPrice || sellPrice === 0)) {
        const margin = Number(((1 - discountedCost / sellPrice) * 100).toFixed(2));
        updatedRowInfo.margin = Number.isFinite(margin) ? margin : null;
    } else if (sellPrice === null) {
        updatedRowInfo.margin = null;
    }
    if ((qty || qty === 0) && (sellPrice || sellPrice === 0)) {
        updatedRowInfo.extendedPrice = Number(sellPrice * qty) / (per || 1);
        updatedRowInfo.extendedDiscountCost = ((discountedCost || 0) * qty) / (per || 1);
    } else if (qty === null || sellPrice === null) {
        updatedRowInfo.extendedPrice = null;
        updatedRowInfo.extendedDiscountCost = null;
    }
    return updatedRowInfo;
}
function handlePerChange({ discountedCost, qty, per, sellPrice }: Pick<Partial<PricingInfo>, 'discountedCost' | 'qty' | 'per' | 'sellPrice'>) {
    const updatedRowInfo: { extendedDiscountCost?: number | null; extendedPrice?: number | null } = {};
    if (qty || qty === 0) {
        updatedRowInfo.extendedDiscountCost = ((discountedCost || 0) * qty) / (per || 100);
        updatedRowInfo.extendedPrice = ((sellPrice || 0) * qty) / (per || 100);
    } else if (qty === null || discountedCost === null) {
        updatedRowInfo.extendedDiscountCost = null;
    }
    return updatedRowInfo;
}

export function handleSellPriceAndRoundingChanges({
    discountedCost,
    sellPrice,
    roundingBehaviourOnCell,
    roundingPrecision
}: Pick<Partial<PricingInfo>, 'discountedCost' | 'sellPrice' | 'roundingPrecision' | 'roundingBehaviourOnCell'>) {
    const updatedRowInfo: { margin?: number | null; sellPrice?: number | null } = {};

    if (roundingBehaviourOnCell === NO_ROUNDING) {
        return updatedRowInfo;
    }
    if (roundingBehaviourOnCell === ROUNDING_SELL_PRICE_KEY && (discountedCost || discountedCost === 0) && (sellPrice || sellPrice === 0)) {
        const roundedSellPrice = Number(applyRounding(sellPrice, roundingPrecision));
        updatedRowInfo.margin = Number(((1 - discountedCost / roundedSellPrice) * 100).toFixed(2));
        updatedRowInfo.sellPrice = roundedSellPrice;
    }
    return updatedRowInfo;
}
export function getUpdatedPricingInfoForChange(currentInfo: PricingInfo, fieldUpdated: BOM_TABLES_ENUM, isRoundingApplied?: boolean) {
    let newPricingInfo = { ...currentInfo };

    switch (fieldUpdated) {
        case BOM_TABLES_ENUM.COST: {
            const updatedPricingInfo = handleCostChange({
                cost: newPricingInfo.cost,
                discount: newPricingInfo.discount,
                margin: newPricingInfo.margin,
                qty: newPricingInfo.qty,
                per: newPricingInfo.per
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        case BOM_TABLES_ENUM.DISCOUNT: {
            const updatedPricingInfo = handleDiscountChange({
                cost: newPricingInfo.cost,
                discount: newPricingInfo.discount,
                margin: newPricingInfo.margin,
                qty: newPricingInfo.qty,
                extendedDiscountCost: newPricingInfo.extendedDiscountCost
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        case BOM_TABLES_ENUM.DISCOUNT_COST: {
            const updatedPricingInfo = handleDiscountedCostChange({
                discountedCost: newPricingInfo.discountedCost,
                cost: newPricingInfo.cost,
                margin: newPricingInfo.margin,
                qty: newPricingInfo.qty,
                per: newPricingInfo.per
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        case BOM_TABLES_ENUM.MARGIN: {
            const updatedPricingInfo = handleMarginChange({
                discountedCost: newPricingInfo.discountedCost,
                margin: newPricingInfo.margin,
                qty: newPricingInfo.qty,
                per: newPricingInfo.per
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        case BOM_TABLES_ENUM.QTY: {
            const updatedPricingInfo = handleSellPriceChange({
                sellPrice: newPricingInfo.sellPrice,
                qty: newPricingInfo.qty,
                per: newPricingInfo.per,
                discountedCost: newPricingInfo.discountedCost
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        case BOM_TABLES_ENUM.SELL_PRICE: {
            const updatedPricingInfo = handleSellPriceChange({
                discountedCost: newPricingInfo.discountedCost,
                sellPrice: newPricingInfo.sellPrice,
                qty: newPricingInfo.qty
            });
            newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
            break;
        }
        default:
            break;
    }

    if (fieldUpdated === BOM_TABLES_ENUM.PER || isRoundingApplied) {
        const updatedPricingInfo = handlePerChange({
            discountedCost: newPricingInfo.discountedCost,
            qty: newPricingInfo.qty,
            per: newPricingInfo.per,
            sellPrice: newPricingInfo.sellPrice
        });
        newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };
    }

    const updatedPricingInfo = handleSellPriceAndRoundingChanges({
        discountedCost: newPricingInfo.discountedCost,
        sellPrice: newPricingInfo.sellPrice,
        roundingBehaviourOnCell: newPricingInfo.roundingBehaviourOnCell,
        roundingPrecision: newPricingInfo.roundingPrecision
    });

    newPricingInfo = { ...newPricingInfo, ...updatedPricingInfo };

    return newPricingInfo;
}

export function getApiDataForPricingInfo(data: Partial<Omit<MainProductDataType, 'qty' | 'type' | 'manufacturer' | 'modelNo' | 'specification' | 'lotInfo'>>) {
    const resultData: Partial<PricingBodInfo> = {};
    for (const colKey of Object.keys(data)) {
        switch (colKey) {
            case BOM_TABLES_ENUM.ID:
                resultData.bod_id = data[colKey];
                break;
            case BOM_TABLES_ENUM.QUOTE_NOTES:
                resultData.notes = data[colKey];
                break;
            case BOM_TABLES_ENUM.LOT:
                resultData.lot_id = data[colKey];
                break;
            case BOM_TABLES_ENUM.COST:
                resultData.cost_cents = data[colKey] || data[colKey] === 0 ? convertToCents(data[colKey] || 0) : data[colKey];
                break;
            case BOM_TABLES_ENUM.DISCOUNT:
                resultData.discount_percentage = data[colKey];
                break;
            case BOM_TABLES_ENUM.DISCOUNT_COST:
                resultData.discounted_cost_cents = data[colKey] || data[colKey] === 0 ? convertToCents(data[colKey] || 0) : data[colKey];
                break;
            case BOM_TABLES_ENUM.EXT_DISCOUNT_COST:
                resultData.extended_discounted_cost_cents = data[colKey] || data[colKey] === 0 ? convertToCents(data[colKey] || 0) : data[colKey];
                break;
            case BOM_TABLES_ENUM.MARGIN:
                resultData.margin_percentage = data[colKey];
                break;
            case BOM_TABLES_ENUM.SELL_PRICE:
                resultData.sell_price_cents = data[colKey] || data[colKey] === 0 ? convertToCents(data[colKey] || 0) : data[colKey];
                break;
            case BOM_TABLES_ENUM.LEAD_TIME_UNIT:
                resultData.lead_time_unit = data[colKey];
                break;
            case BOM_TABLES_ENUM.LEAD_TIME_MIN:
                resultData.lead_time_min = data[colKey];
                break;
            case BOM_TABLES_ENUM.LEAD_TIME_MAX:
                resultData.lead_time_max = data[colKey];
                break;
            case BOM_TABLES_ENUM.QUOTE_NO:
                resultData.source_quote_number = data[colKey];
                break;
            case BOM_TABLES_ENUM.INTERNAL_COMMENTS:
                resultData.internal_notes = data[colKey];
                break;
            case BOM_TABLES_ENUM.PER:
                resultData.per = data[colKey] || 1;
                break;
        }
    }

    return resultData;
}

export function getApiDataForBomInfo(
    data: Partial<Pick<MainProductDataType, 'qty' | 'category' | 'manufacturer' | 'model_number' | 'description' | 'productFinder' | 'specification' | 'description' | 'id'>> &
        Partial<Pick<OptionDataType, 'replaceProductId'>>
) {
    const resultData: updateBomDataRequest['input'] = {};
    for (const colKey of Object.keys(data)) {
        switch (colKey) {
            case BOM_TABLES_ENUM.ID:
                resultData.id = data[colKey];
                break;
            case BOM_TABLES_ENUM.QTY:
                resultData.qty = data[colKey];
                break;
            case BOM_TABLES_ENUM.TYPE:
                resultData.category = data[colKey];
                break;
            case BOM_TABLES_ENUM.MANUFACTURER:
                resultData.manufacturer = data[colKey];
                break;
            case BOM_TABLES_ENUM.MODEL_NO:
                resultData.model_number = data[colKey];
                break;
            case BOM_TABLES_ENUM.DESCRIPTION:
                resultData.description = data[colKey];
                break;
            case BOM_TABLES_ENUM.PRODUCT_FINDER:
                resultData.productFinder = data[colKey];
                break;
            case BOM_TABLES_ENUM.SPECIFICATION:
                resultData.specification = data[colKey];
                break;
            case BOM_TABLES_ENUM.PRODUCT_TO_REPLACE:
                resultData.product_to_replace = data[colKey];
                break;
        }
    }

    return resultData;
}

export function getCentsToDollars(orderTotal: number) {
    const orderAmountInDollars = orderTotal / 100;
    return orderAmountInDollars;
}

export function getPercentageAmount(amount: number, percentage: number) {
    return Number((amount * (percentage / 100)).toFixed(2));
}

export function getServicesTotal(servicesData?: ServicesResponseType, orderTotal = 0) {
    if (!servicesData || servicesData.length === 0) {
        return null;
    }

    return servicesData.reduce((currSum: number | null, currService) => {
        let currServiceAmount: number | null = currSum;
        if (currService.calculation_method === CALC_METHODS.FLAT_FEE && currService.flat_fee_cents !== null) {
            currServiceAmount = Number(currServiceAmount) + convertToDollar(currService.flat_fee_cents);
        } else if (currService.calculation_method === CALC_METHODS.PERCENTAGE_OF_ORDER && currService.percentage_of_order !== null) {
            currServiceAmount = Number(currServiceAmount) + getPercentageAmount(orderTotal, currService.percentage_of_order);
        }
        return currServiceAmount;
    }, null);
}

export function getPercentageFieldAccessor(field: string, data: Record<string, any>, column: any) {
    const fieldData = data[field];
    if (fieldData === null) {
        return fieldData;
    }

    const intl = new Internationalization();
    const val = intl.formatNumber(data[field], { format: 'N2', useGrouping: false, minimumFractionDigits: 2 });

    if (column?.parent?.isEdit) {
        return val;
    }
    return `${val}%`;
}

interface FooterDataObj {
    totalExtendedPrice?: number;
    totalCost?: number;
    totalExtDiscountedCost?: number;
    totalMarginCost?: number;
    totalMarginPercentage?: number;
    grandTotal?: number;
}

export const getPriceStats = (footerData: FooterDataObj) => {
    const { totalExtendedPrice = 0, totalCost = 0, totalExtDiscountedCost = 0, totalMarginCost = 0, totalMarginPercentage = 0, grandTotal = 0 } = footerData;
    return [
        {
            title: 'EXT. COST',
            count: `$${getAmountForUi(totalCost)}`
        },
        {
            title: 'EXT. DIS. COST',
            count: `$${getAmountForUi(totalExtDiscountedCost)}`
        },
        {
            title: 'MARGIN',
            count: `$${getAmountForUi(totalMarginCost)}`,
            helperText: `${getAmountForUi(totalMarginPercentage)}%`,
            helperTextColor: 'primary.contrastText'
        },
        {
            title: 'EXT. SELL',
            count: `$${getAmountForUi(totalExtendedPrice)}`
        },
        {
            title: 'TOTAL',
            count: `$${getAmountForUi(grandTotal)}`,
            titleColor: 'tertiary.light',
            countColor: 'tertiary.main'
        }
    ];
};
